import { SpokeSpinWrapper } from '@doltech/core/lib/auth/SpokeSpinWrapper';
import { IconOtherSearch } from '@doltech/icons/IconOtherSearch';
import { Modal } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import cl from 'classnames';
import * as React from 'react';
import { SortableContainer, SortableContainerProps, SortableElement } from 'react-sortable-hoc';
import { useUpdateEffect } from 'react-use';
import styled from 'styled-components';

import { Button } from '../../../figma/ButtonV2/ButtonV2';
import { Chips } from '../../../figma/Chips/Chips';
import { colorsV2 } from '../../../figma/colors-v2';
import { Typography } from '../../../figma/DesignSystemV2/Typography';
import { Input } from '../../../figma/Form/InputV2/InputV2';
import { layoutGridOption, layoutGridWidthCalculator } from '../../../hooks/useLayoutGrid';

const MODAL_WIDTH = layoutGridWidthCalculator(10) + layoutGridOption.gutter * 2;

const ListingSection = styled.section<{ numCardColumnPerRow?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.numCardColumnPerRow || 3}, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 24px;
  overflow: scroll;
`;

const FilterSection = styled.section`
  display: grid;
  row-gap: 24px;

  .filter-group {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fit, minmax(0, max-content));
    column-gap: 24px;

    .search-box {
      width: 320px;
    }

    .filter-selection {
      display: grid;
      grid-auto-flow: column;
      column-gap: 16px;
    }
  }
`;

const TopSection = styled.section`
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: center;
`;

const BodyContainer = styled.section`
  display: grid;
  row-gap: 24px;
`;

const SortableItem = SortableElement<any>(({ children }) => children);

const SortableList = SortableContainer<any>((props) => {
  const { data, renderCard, disabled, numCardColumnPerRow = 3 } = props;

  return (
    <ListingSection numCardColumnPerRow={numCardColumnPerRow} className="listing-section">
      {(data || [])?.map((listing, index) => (
        <SortableItem key={listing?.id || index} index={index} disabled={disabled}>
          {renderCard(listing, index)}
        </SortableItem>
      ))}
    </ListingSection>
  );
});

export interface SelectionFilterModel {
  key?: string;
  group?: 'main' | 'sub';
  render?: () => React.ReactNode;
  selected?: boolean;
}

export interface TrendingSelectionModalProps extends ModalProps {
  data?: any;
  selectionFilters?: SelectionFilterModel[];
  paginationElement?: React.ReactNode;
  renderCard?: (listing: any) => React.ReactNode;
  onCancel?: (e?: React.MouseEvent<HTMLElement>) => void;
  onRender?: () => void;
  onSearch?: (searchValue?: string) => void;
  onFilterSelect?: (key?: string) => void;
  onSortEnd?: SortableContainerProps['onSortEnd'];
  onApply?: () => void;
  searchBoxDisabled?: boolean;
  applyButtonDisabled?: boolean;
  loading?: boolean;
  sortable?: boolean;
  defaultSelectedFilterKey?: string;
  numCardColumnPerRow?: number;
}

export const TrendingSelectionModal = (props: TrendingSelectionModalProps) => {
  const {
    data,
    selectionFilters = [],
    renderCard,
    paginationElement,
    onCancel,
    onRender,
    onSearch,
    onFilterSelect,
    onSortEnd,
    onApply,
    visible = false,
    sortable = false,
    searchBoxDisabled = false,
    applyButtonDisabled = false,
    loading = false,
    defaultSelectedFilterKey = '',
    numCardColumnPerRow = 3,
  } = props;
  const emptyList = new Array(12).fill(0);
  const mainGroupFilters = selectionFilters.filter(({ group }) => !group || group === 'main');
  const subGroupFilters = selectionFilters.filter(({ group }) => group === 'sub');

  const [selectedFilter, setSelectedFilter] = React.useState(defaultSelectedFilterKey);

  useUpdateEffect(() => {
    if (visible) {
      onRender?.();
    }
  }, [visible]);

  const renderFilters = React.useCallback(
    (groupFilters) => {
      return groupFilters.map(({ key, selected, render }) => (
        <Chips
          key={key}
          activeColor={colorsV2.blue100}
          type={selected || selectedFilter === key ? 'on' : 'off'}
          onClick={() => {
            setSelectedFilter(key);
            onFilterSelect(key);
          }}
        >
          {render()}
        </Chips>
      ));
    },
    [onFilterSelect, selectedFilter]
  );

  return (
    <Modal
      className={cl('dol-modal', 'trending-selection-modal')}
      width={MODAL_WIDTH}
      onCancel={onCancel}
      open={visible}
      closable={false}
      footer={null}
      destroyOnClose
      maskClosable
    >
      <SpokeSpinWrapper spinning={loading}>
        <BodyContainer>
          <TopSection>
            <Typography.Title level={3} variant="semi-bold/24">
              Select Trending Card
            </Typography.Title>
            <Button.Primary
              onClick={() => {
                onApply();
                onCancel();
              }}
              disabled={applyButtonDisabled}
            >
              Apply
            </Button.Primary>
          </TopSection>

          <FilterSection>
            <div className="filter-group">
              <Input
                type="on-gray"
                className="dol-input search-box"
                placeholder="Search"
                onBlur={(e) => onSearch(e.target.defaultValue)}
                onPressEnter={(e: any) => onSearch(e.target.defaultValue)}
                prefix={<IconOtherSearch style={{ color: colorsV2.gray100 }} />}
                disabled={searchBoxDisabled}
              />
              <div className="filter-selection main">{renderFilters(mainGroupFilters)}</div>
            </div>

            <div className="filter-group">
              <div className="filter-selection sub">{renderFilters(subGroupFilters)}</div>
            </div>
          </FilterSection>

          <SortableList
            onSortEnd={onSortEnd}
            data={data || emptyList}
            renderCard={renderCard}
            disabled={!sortable}
            distance={2}
            numCardColumnPerRow={numCardColumnPerRow}
            axis="xy"
          />
          {paginationElement}
        </BodyContainer>
      </SpokeSpinWrapper>
    </Modal>
  );
};
